'use client';

/**
 * Interactive exhibits for v2 guides: one idea on screen at a time, the reader flips between them.
 * - GuideRentedOwned: one browser window that flips between a directory page and the loan officer's own website.
 * - GuideJourney: five steps from an AI answer to a funded loan, one scene at a time.
 * - GuideVisitors: one grid of 1,000 visitors that flips between a typical and a well-optimized website.
 * Every word of every state is server-rendered (inactive states carry `hidden`), so crawlers and AI tools read all of it.
 * Auto-advance runs only while the exhibit is on screen, stops for good at the reader's first click, and never runs for visitors
 * who ask for reduced motion. The mini web pages are drawn as light "screenshots" in both themes, so their text is always dark on light.
 * All figures arrive as props from the page's MDX (docs/content/ai-visibility-pillar/visuals.json).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { nb } from '@/lib/nobreak';

function useAutoAdvance(count: number, ms: number) {
  const [i, setI] = useState(0);
  const [stopped, setStopped] = useState(false);
  const ref = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (stopped || count < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { setStopped(true); return; }
    let visible = false;
    const el = ref.current;
    const io = el ? new IntersectionObserver(([e]) => { visible = e.isIntersecting && e.intersectionRatio > 0.5; }, { threshold: [0, 0.5, 1] }) : null;
    if (el && io) io.observe(el);
    const t = setInterval(() => { if (visible && !document.hidden) setI((v) => (v + 1) % count); }, ms);
    return () => { clearInterval(t); io?.disconnect(); };
  }, [count, ms, stopped]);
  const pick = useCallback((n: number) => { setStopped(true); setI(n); }, []);
  return { i, pick, ref, stopped, stop: () => setStopped(true) };
}

function Switch({ options, active, onPick, label }: { options: string[]; active: number; onPick: (n: number) => void; label: string }) {
  return (
    <div className="gf-sw" role="tablist" aria-label={label} style={{ ['--n' as string]: options.length, ['--a' as string]: active } as React.CSSProperties}>
      <span className="gf-sw-pill" aria-hidden="true" />
      {options.map((o, n) => (
        <button key={o} type="button" role="tab" aria-selected={active === n} className={active === n ? 'on' : undefined} onClick={() => onPick(n)}>{o}</button>
      ))}
    </div>
  );
}

const LinkIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 14a4 4 0 005.66 0l3-3a4 4 0 00-5.66-5.66l-1 1" /><path d="M14 10a4 4 0 00-5.66 0l-3 3a4 4 0 005.66 5.66l1-1" /></svg>
);
const Star = () => <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M10 1.8l2.5 5.2 5.7.8-4.1 4 1 5.7L10 14.8l-5.1 2.7 1-5.7-4.1-4 5.7-.8z" fill="currentColor" /></svg>;
const Stars = () => <span className="gm-stars"><Star /><Star /><Star /><Star /><Star /></span>;
const Check = () => <svg viewBox="0 0 20 20" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 10.5l3.2 3.2L15 7" /></svg>;

/* ---------- the two mini web pages (decorative; the captions carry the meaning) */
function DirectoryPage({ city }: { city: string }) {
  return (
    <div className="gm gm-dir" aria-hidden="true">
      <div className="gm-nav"><b>Lender Directory</b><span>Lenders</span><span>Reviews</span><span>Rates</span></div>
      <div className="gm-dir-h">Best mortgage lenders in {city}</div>
      <div className="gm-dir-g">
        <div className="gm-dir-list">
          {['Lender A', 'You', 'Lender B', 'Lender C'].map((n) => (
            <div key={n} className={`gm-row${n === 'You' ? ' is-you' : ''}`}>
              <span className="gm-av" />
              <span className="gm-row-t"><b>{n === 'You' ? 'Your listing' : n}</b><Stars /></span>
              <span className="gm-row-b">View</span>
            </div>
          ))}
        </div>
        <div className="gm-quote">
          <b>Compare quotes from other lenders</b>
          <span className="gm-in">Your name</span>
          <span className="gm-in">Phone</span>
          <span className="gm-btn is-dir">Get my quotes</span>
        </div>
      </div>
    </div>
  );
}

function SitePage({ city, name }: { city: string; name: string }) {
  return (
    <div className="gm gm-site" aria-hidden="true">
      <div className="gm-nav is-site"><b>{name}</b><span>Loans</span><span>About</span><span className="gm-pillnav">Talk to me</span></div>
      <div className="gm-site-g">
        <div className="gm-hero">
          <span className="gm-kick">FHA loans in {city}</span>
          <span className="gm-hl">Your home loan, handled by someone who answers.</span>
          <span className="gm-btn is-site">See my options</span>
          <span className="gm-trust"><Stars /> Your reviews from local families</span>
        </div>
        <div className="gm-quote is-site">
          <b>Get your options</b>
          <span className="gm-in">Your name</span>
          <span className="gm-in">Phone</span>
          <span className="gm-btn is-site">Send it to me</span>
        </div>
      </div>
    </div>
  );
}

type Place = { tab: string; url: string; headline: string; stat: string; todo: string };

export function GuideRentedOwned({ title, city, name, rented, owned, note }: { title: string; city: string; name: string; rented: Place; owned: Place; note?: string }) {
  const { i, pick, ref } = useAutoAdvance(2, 5200);
  const places = [rented, owned];
  return (
    <figure className="gx gf gf-ro" ref={ref as React.RefObject<HTMLElement>} data-i={i}>
      <figcaption className="gf-title">{nb(title)}</figcaption>
      <Switch label={title} options={places.map((p) => p.tab)} active={i} onPick={pick} />
      <div className="gf-browser">
        <div className="gf-chrome"><i /><i /><i /><span className="gf-url">{places[i].url}</span></div>
        <div className="gf-screen">
          <div className={`gf-layer${i === 0 ? ' on' : ''}`}><DirectoryPage city={city} /></div>
          <div className={`gf-layer${i === 1 ? ' on' : ''}`}><SitePage city={city} name={name} /></div>
        </div>
      </div>
      {places.map((p, n) => (
        <div key={p.tab} className={`gf-cap is-${n === 0 ? 'rented' : 'owned'}`} hidden={i !== n}>
          <p className="gf-hl">{nb(p.headline)}</p>
          <p className="gf-stat">{nb(p.stat)}</p>
          <p className="gf-todo">{nb(p.todo)}</p>
        </div>
      ))}
      {note && <p className="gf-note">{nb(note)}</p>}
    </figure>
  );
}

/* ---------- the journey: one scene at a time */
type Step = { short: string; t: string; s: string; zone: 'rented' | 'owned'; kind: 'ai' | 'directory' | 'site' | 'lead' | 'funded'; big?: string; bigSub?: string; question?: string };

function JourneyScene({ step, city, name }: { step: Step; city: string; name: string }) {
  switch (step.kind) {
    case 'ai':
      return (
        <div className="gj-sc gj-ai" aria-hidden="true">
          <span className="gj-bubble is-q">{step.question}</span>
          <span className="gj-bubble is-a"><i /><i /><i className="s" /></span>
          <span className="gj-src">Sources <em className="on">Directory</em><em>Review site</em><em>Lender site</em></span>
        </div>
      );
    case 'directory':
      return <div className="gj-sc gj-shot" aria-hidden="true"><DirectoryPage city={city} /></div>;
    case 'site':
      return (
        <div className="gj-sc gj-shot" aria-hidden="true">
          <span className="gj-via"><LinkIcon />Via the link on your profile</span>
          <SitePage city={city} name={name} />
        </div>
      );
    default:
      return (
        <div className={`gj-sc gj-big is-${step.kind}`}>
          <span className="gj-badge" aria-hidden="true"><Check />{step.kind === 'lead' ? 'New lead' : 'Funded'}</span>
          <span className="gj-num">{nb(step.big || '')}</span>
          <span className="gj-numsub">{nb(step.bigSub || '')}</span>
        </div>
      );
  }
}

export function GuideJourney({ title, city, name, steps, note }: { title: string; city: string; name: string; steps: Step[]; note?: string }) {
  const { i, pick, ref } = useAutoAdvance(steps.length, 4600);
  const firstOwned = steps.findIndex((s) => s.zone === 'owned');
  return (
    <figure className="gx gf gf-jn" ref={ref as React.RefObject<HTMLElement>}>
      <figcaption className="gf-title">{nb(title)}</figcaption>
      <ol className="gj-rail" style={{ ['--n' as string]: steps.length, ['--a' as string]: i, ['--o' as string]: firstOwned } as React.CSSProperties}>
        {steps.map((s, n) => (
          <li key={s.short} className={`is-${s.zone}${n === i ? ' on' : ''}${n < i ? ' done' : ''}${n === firstOwned ? ' is-switch' : ''}`}>
            {n === firstOwned && <span className="gj-link" aria-hidden="true"><LinkIcon /></span>}
            <button type="button" onClick={() => pick(n)} aria-current={n === i ? 'step' : undefined} aria-label={`Step ${n + 1}: ${s.t}`}>
              <span className="gj-dot" aria-hidden="true" />
              <span className="gj-short">{s.short}</span>
            </button>
          </li>
        ))}
      </ol>
      <div className={`gj-stage is-${steps[i].zone}`}>
        <span className="gj-where">{steps[i].kind === 'ai' ? 'In the AI answer' : steps[i].zone === 'rented' ? 'On the directory’s page' : 'On your own website'}</span>
        {steps.map((s, n) => (
          <div key={s.short} className={`gj-layer${n === i ? ' on' : ''}`}><JourneyScene step={s} city={city} name={name} /></div>
        ))}
      </div>
      {steps.map((s, n) => (
        <div key={s.short} className="gj-cap" hidden={n !== i}>
          <p className="gj-t">{nb(s.t)}</p>
          <p className="gj-s">{nb(s.s)}</p>
        </div>
      ))}
      <div className="gj-nav">
        <button type="button" onClick={() => pick((i - 1 + steps.length) % steps.length)} aria-label="Previous step">Back</button>
        <span>{nb(`Step ${i + 1} of ${steps.length}`)}</span>
        <button type="button" className="is-next" onClick={() => pick((i + 1) % steps.length)} aria-label="Next step">Next</button>
      </div>
      {note && <p className="gf-note">{nb(note)}</p>}
    </figure>
  );
}

/* ---------- 1,000 visitors: one grid, flip between the two websites */
type Site = { tab: string; rate: string; leads: [number, number]; funded: [number, number]; line: string };

function cells(seed: number): Array<[number, number]> {
  const all: Array<[number, number]> = [];
  for (let y = 0; y < 20; y++) for (let x = 0; x < 50; x++) all.push([x, y]);
  let s = seed;
  for (let k = all.length - 1; k > 0; k--) { s = (s * 1103515245 + 12345) % 2147483648; const j = s % (k + 1); [all[k], all[j]] = [all[j], all[k]]; }
  return all;
}
const ORDER = cells(48271);

export function GuideVisitors({ title, sites, note }: { title: string; sites: Site[]; note?: string }) {
  const { i, pick, ref } = useAutoAdvance(sites.length, 5200);
  const s = sites[i];
  const maxLeads = Math.max(...sites.map((x) => x.leads[0]));
  return (
    <figure className="gx gf gf-vs" ref={ref as React.RefObject<HTMLElement>}>
      <figcaption className="gf-title">{nb(title)}</figcaption>
      <Switch label={title} options={sites.map((x) => x.tab)} active={i} onPick={pick} />
      <div className="gfv-dots" aria-hidden="true">
        {ORDER.slice(0, maxLeads).map(([x, y], n) => (
          <i key={`${x}-${y}`} className={`${n < s.leads[0] ? 'lead' : ''}${n < s.funded[0] ? ' fund' : ''}`} style={{ ['--x' as string]: x, ['--y' as string]: y, ['--x2' as string]: (y * 50 + x) % 25, ['--y2' as string]: Math.floor((y * 50 + x) / 25), ['--d' as string]: `${(n % 25) * 18}ms` } as React.CSSProperties} />
        ))}
      </div>
      <p className="gfv-key" aria-hidden="true"><span className="k-lead" />A visitor who became a lead <span className="k-fund" />A lead who closed <span className="k-dim" />A visitor who left</p>
      {sites.map((x, n) => (
        <div key={x.tab} className="gfv-out" hidden={n !== i}>
          <p className="gfv-rate">{nb(x.rate)}</p>
          <dl>
            <div><dt>Leads</dt><dd>{nb(`${x.leads[0]}–${x.leads[1]}`)}</dd></div>
            <div className="f"><dt>Funded loans</dt><dd>{nb(`${x.funded[0]}–${x.funded[1]}`)}</dd></div>
          </dl>
          <p className="gfv-line">{nb(x.line)}</p>
        </div>
      ))}
      {note && <p className="gf-note">{nb(note)}</p>}
    </figure>
  );
}
